import { TopicType } from '../../types/topic';

/**
 * Input structure for creating a topic
 */
export interface CreateTopicInput {
  projectId: string;
  name: string;
  description: string;
  topicType: TopicType;
}

/**
 * Input structure for updating a topic
 */
export interface UpdateTopicInput {
  name?: string;
  description?: string;
  topicType?: TopicType;
  lastUsedAt?: string;
  messages?: Array<{
    id: string;
    content: string;
    role: 'user' | 'assistant';
    timestamp: number;
  }>;
}

/**
 * Input for appending a message to a chat topic
 */
export interface AppendMessageInput {
  content: string;
  role: 'user' | 'assistant';
  timestamp: number;
}

/**
 * Formatted topic data returned by the useTopics hook
 * Includes derived properties and UI state
 */
export interface FormattedTopic {
  id: string;
  projectId: string;
  name: string;
  description: string;
  topicType: TopicType;
  createdAt: string;
  updatedAt?: string;
  lastUsedAt?: string;
  messages: Array<{
    id: string;
    content: string;
    role: 'user' | 'assistant';
    timestamp: number;
  }>;
  // UI state or derived properties
  isActive?: boolean;
}

/**
 * Raw topic data as returned from the database
 */
export interface TopicData {
  id: string;
  project_id: string;
  name: string;
  description: string;
  topic_type: TopicType;
  created_at: string;
  updated_at?: string;
  last_used_at?: string;
  messages: Array<{
    id: string;
    content: string;
    role: 'user' | 'assistant';
    timestamp: number;
  }>;
  // Any additional fields from the database
  metadata?: Record<string, unknown>;
}

/**
 * Helper function to format topic data from the database
 * into a consistent structure for the app
 */
export function formatTopic(topic: TopicData, activeTopicId?: string | null): FormattedTopic {
  return {
    id: topic.id,
    projectId: topic.project_id,
    name: topic.name,
    description: topic.description,
    topicType: topic.topic_type,
    createdAt: topic.created_at,
    updatedAt: topic.updated_at,
    lastUsedAt: topic.last_used_at,
    messages: topic.messages || [],
    // Optional UI state
    isActive: activeTopicId ? topic.id === activeTopicId : false,
  };
}